import React, { useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Environment, Float } from '@react-three/drei';
import * as THREE from 'three';

function DNAHelix() {
    const groupRef = useRef();
    const materialRef = useRef();

    // Generate the DNA structure
    const { points, lines } = useMemo(() => {
        const numPairs = 50;
        const radius = 1.8;
        const height = 7;
        const pointsArr = [];
        const linesArr = [];

        for (let i = 0; i < numPairs; i++) {
            const t = (i / (numPairs - 1)) * Math.PI * 4;
            const y = (i / (numPairs - 1)) * height - height / 2;

            const xA = Math.cos(t) * radius;
            const zA = Math.sin(t) * radius;
            pointsArr.push(xA, y, zA);

            const xB = Math.cos(t + Math.PI) * radius;
            const zB = Math.sin(t + Math.PI) * radius;
            pointsArr.push(xB, y, zB);

            linesArr.push(new THREE.Vector3(xA, y, zA));
            linesArr.push(new THREE.Vector3(xB, y, zB));
        }
        return { points: new Float32Array(pointsArr), lines: linesArr };
    }, []);

    const lineGeometry = useMemo(() => {
        return new THREE.BufferGeometry().setFromPoints(lines);
    }, [lines]);

    useFrame((state, delta) => {
        if (groupRef.current) {
            // 1. Standard rotation
            groupRef.current.rotation.y += delta * 0.2;
            
            // 2. INNOVATION: Mouse Parallax (Tilt towards mouse)
            // Map mouse X (-1 to 1) to rotation Z (-0.2 to 0.2)
            const targetRotationZ = (state.mouse.x * Math.PI) / 10;
            // Map mouse Y (-1 to 1) to rotation X (-0.2 to 0.2)
            const targetRotationX = (state.mouse.y * Math.PI) / 10;

            // Smoothly interpolate current rotation to target rotation
            groupRef.current.rotation.z = THREE.MathUtils.lerp(groupRef.current.rotation.z, targetRotationZ, 0.05);
            groupRef.current.rotation.x = THREE.MathUtils.lerp(groupRef.current.rotation.x, targetRotationX, 0.05);
        }

        // 2. INNOVATION: Pulsing Glow
        if (materialRef.current) {
            materialRef.current.opacity = 0.7 + Math.sin(state.clock.elapsedTime * 2) * 0.2;
        }
    });

    return (
        <Float speed={1.5} rotationIntensity={0.2} floatIntensity={1}>
            <group ref={groupRef} scale={1.2}>
                <points>
                    <bufferGeometry>
                        <bufferAttribute attach="attributes-position" count={points.length / 3} array={points} itemSize={3} />
                    </bufferGeometry>
                    <pointsMaterial 
                        ref={materialRef}
                        color="#ffffff" 
                        size={0.08} 
                        sizeAttenuation={true} 
                        transparent 
                        opacity={0.8} 
                    />
                </points>

                <lineSegments geometry={lineGeometry}>
                    <lineBasicMaterial color="#ffffff" transparent opacity={0.15} />
                </lineSegments>
            </group>
        </Float>
    );
}

export default function Hero3D() {
    return (
        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1, pointerEvents: 'none' }}>
            <Canvas camera={{ position: [0, 0, 8], fov: 45 }} dpr={[1, 1.5]}>
                <ambientLight intensity={0.5} />
                <DNAHelix />
                <Environment preset="city" />
            </Canvas>
        </div>
    );
}